import { createColumnHelper } from "@tanstack/react-table";
import { PencilSquareIcon } from "@heroicons/react/24/solid";
import {
  LockClosedIcon,
  LockOpenIcon,
  ArrowUturnLeftIcon,
  ShareIcon,
  LinkIcon,
  LinkSlashIcon,
  PlusIcon,
  BoltIcon,
  DevicePhoneMobileIcon,
  NoSymbolIcon,
  ArrowsRightLeftIcon,
  TrashIcon,
  AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/outline";

const columnHelper = createColumnHelper();

const formatDateNew = (dateNum) => {
  const str = dateNum?.toString();
  if (!str || str.length !== 8) return str; // fallback for invalid format

  const year = str.slice(0, 4);
  const month = str.slice(4, 6);
  const day = str.slice(6, 8);

  const date = new Date(`${year}-${month}-${day}`);

  const dayStr = date.toLocaleString("en-GB", { day: "2-digit" });
  const monthStr = date
    .toLocaleString("en-GB", { month: "short" })
    .replace(/^./, (ch) => ch.toUpperCase()); // Capitalize first letter
  const yearStr = date.toLocaleString("en-GB", { year: "2-digit" });

  return `${dayStr}-${monthStr}-${yearStr}`; // → "08-Jun-25"
};

function formatEpochToDateTime(epochTimestamp) {
  const date = new Date(epochTimestamp);

  const day = String(date.getDate()).padStart(2, "0");
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = String(date.getFullYear()).slice(-2); // Last 2 digits

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";

  hours = hours % 12;
  hours = hours === 0 ? 12 : hours; // Convert 0 to 12 for 12-hour format

  return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
}

// 🌐
const WhatsAppShareButton = (props) => {
  const { username, password, role, points, endAt, android, windows, ios } =
    props;
  // console.log("WhatsApp Data: ", props);
  const isPoints = !!points;
  const isSubscriber = !!endAt;

  const expiryOrPointsMessage = isPoints
    ? `🎯 *Points:* ${points}\n`
    : isSubscriber
      ? `⏳ *Expiry:* ${formatDateNew(endAt)}\n`
      : "";

  const handleShare = () => {
    // base message
    let message =
      "🔒 *Website Credentials*\n\n" +
      `👦 *Username:* ${username}\n` +
      `🔑 *Password:* ${password}\n` +
      expiryOrPointsMessage +
      `👦 *Role:* ${role}\n`;

    // Downloads page for all users
    message += `\n⬇️ *Downloads:* ${window.location.origin}/downloads\n`;

    // if NOT a subscriber, include dashboard link
    if (!isSubscriber) {
      message += `\n🌐 *Dashboard:* ${window.location.origin}\n`;
    }

    // add download links only if available
    let appsMessage = "";

    if (android) appsMessage += `\n• *Android:* ${android}\n`;
    if (windows) appsMessage += `\n• *WINDOWS:* ${windows}\n`;
    if (ios) appsMessage += `\n• *Apple:* ${ios}\n`;

    if (appsMessage) {
      message += "\n🔗 *APPS:*" + appsMessage;
    }

    const encodedMessage = encodeURIComponent(message);
    const url = `https://api.whatsapp.com/send/?phone=&text=${encodedMessage}`;
    window.open(url, "_blank");
  };

  return (
    <li className="inline-block cursor-pointer order-9">
      <div className="relative group">
        <button
          onClick={handleShare}
          className="w-5 h-5 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center shadow-md p-0"
        >
          {/* WhatsApp Icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
            className="w-3.5 h-3.5 text-white"
            fill="black"
          >
            <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7 .9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
          </svg>
        </button>

        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
          <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
            Share on WhatsApp
          </div>
          <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
        </div>
      </div>
    </li>
  );
};

const UniversalShareButton = (props) => {
  const { username, password, role, points, endAt, android, windows, ios } =
    props;

  const isPoints = !!points;
  const isSubscriber = !!endAt;

  const expiryOrPointsMessage = isPoints
    ? `🎯 Points: ${points}\n`
    : isSubscriber
      ? `⏳ Expiry: ${formatDateNew(endAt)}\n`
      : "";

  const handleShare = async () => {
    // base message
    let message =
      "🔒 Website Credentials\n\n" +
      `👦 Username: ${username}\n` +
      `🔑 Password: ${password}\n` +
      expiryOrPointsMessage +
      `👦 Role: ${role}\n`;

    // Downloads page for all users
    message += `\n⬇️ *Downloads:* ${window.location.origin}/downloads\n`;

    // if NOT a subscriber, include dashboard link
    if (!isSubscriber) {
      message += `\n🌐 *Dashboard:* ${window.location.origin}\n`;
    }

    // add download links only if available
    let appsMessage = "";
    if (android) appsMessage += `\n• ANDROID: ${android}\n`;
    if (windows) appsMessage += `\n• WINDOWS EXE: ${windows}\n`;
    if (ios) appsMessage += `\n• Apple: ${ios}\n`;

    if (appsMessage) {
      message += "\n🔗 APPS:" + appsMessage;
    }

    message = message.trim(); // cleanup trailing spaces/newlines

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Website Credentials",
          text: message,
        });
      } catch (error) {
        console.error("Share failed:", error);
      }
    } else {
      // fallback: open WhatsApp share link
      const encodedMessage = encodeURIComponent(message);
      const url = `https://api.whatsapp.com/send?text=${encodedMessage}`;
      window.open(url, "_blank");
    }
  };

  return (
    <li className="inline-block cursor-pointer order-10">
      <div className="relative group">
        <button
          onClick={handleShare}
          className="w-5 h-5 rounded-full bg-cyan-500 hover:bg-cyan-600 text-white flex items-center justify-center shadow-md p-0"
        >
          <ShareIcon className="w-3.5 h-3.5 text-gray-800" />
        </button>
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
          <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
            Universal Share
          </div>
          <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
        </div>
      </div>
    </li>
  );
};

const reusableColumns = [
  {
    key: "id",
    header: "ID",
    enableColumnFilter: false,
    render: (info) => (
      <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
        {info.getValue()}
      </h1>
    ),
  },
  {
    key: "username",
    header: "Username",
    enableColumnFilter: true,
    render: (info) => (
      <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
        {info.getValue()}
      </h1>
    ),
    meta: { placeholderName: "Find Username" },
  },
  {
    key: "name",
    header: "Name",
    enableColumnFilter: true,
    render: (info) => (
      <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
        {info.getValue()}
      </h1>
    ),
    meta: { placeholderName: "Find Name" },
  },
  {
    key: "password",
    header: "Password",
    enableColumnFilter: false,
    render: (info) => (
      <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
        {info.getValue()}
      </h1>
    ),
  },
  {
    key: "enabled",
    header: "Status",
    enableColumnFilter: true,
    render: (info) => {
      const isActive = info.getValue();
      return (
        <span
          className={`px-2 py-1 text-sm font-semibold rounded-full text-center ${
            isActive ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
          }`}
        >
          {isActive ? "Active" : "Inactive"}
        </span>
      );
    },
    meta: {
      filterVariant: "select",
      filterOptions: {
        "All Status": "",
        Active: true,
        Inactive: false,
      },
    },
  },
  // {
  //   key: "lastSeen",
  //   header: "Last Seen",
  //   enableColumnFilter: false,
  //   render: (info) => {
  //     const DateString = info.getValue();
  //     if (!DateString) {
  //       return "";
  //     }

  //     const formattedDate = DateString.split(".")[0]; // Remove microseconds part
  //     const expiryDate = new Date(formattedDate); // Create Date object
  //     const timeLeft = formatDistanceToNow(expiryDate);

  //     // Remove "about" from the formatted string
  //     const cleanedTimeLeft = timeLeft.replace(/^about\s/, "");

  //     return (
  //       <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
  //         {cleanedTimeLeft} ago
  //       </h1>
  //     );
  //   },
  // },
  // {
  //   key: "createdAt",
  //   header: "Created At",
  //   enableColumnFilter: false,
  //   render: (info) => (
  //     <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
  //       {formatDateNew(info.getValue())}
  //     </h1>
  //   ),
  // },
];

const tableColumns = {
  owners: [
    {
      key: "points",
      header: "Points",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      enableColumnFilter: false,
      render: ({ row, callback }) =>
        generateActionButtonsJsx(row, callback, "owners", true),
    },
  ],
  refunds: [
    {
      key: "id",
      header: "ID",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "username",
      header: "Username",
      enableColumnFilter: true,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
      meta: { placeholderName: "Find Username" },
    },
    {
      key: "points",
      header: "Points",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "parent",
      header: "Parent",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
      meta: { placeholderName: "Search Parent" },
    },
    {
      key: "subscriptionName",
      header: "Subscription",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "subscriptionStartedAt",
      header: "Started At",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {formatDateNew(info.getValue())}
        </h1>
      ),
    },
    {
      key: "type",
      header: "Type",
      enableColumnFilter: true,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
      meta: {
        defaultValue: "Refund Type",
        filterVariant: "select",
        filterOptions: {
          "Refund Type": "",
          Partial: "Partial",
          Full: "Full",
        },
      },
    },
    {
      key: "refundingMonths",
      header: "Refunding",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()} {info.getValue() === 1 ? "Month" : "Months"}
        </h1>
      ),
    },
    {
      key: "reason",
      header: "Reason",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "status",
      header: "Status",
      enableColumnFilter: true,
      render: (info) => {
        const status = info.getValue();
        const statusStyles = {
          PENDING: "text-orange-600 bg-orange-100",
          REFUNDED: "text-green-600 bg-green-100",
          REJECTED: "text-red-600 bg-red-100",
        };

        return (
          <span
            className={`px-3 py-1 rounded-full text-center text-sm font-medium ${
              statusStyles[status] || "text-gray-500 bg-gray-100"
            }`}
          >
            {status}
          </span>
        );
      },
      meta: {
        defaultValue: "All Status",
        filterVariant: "select",
        filterOptions: {
          "All Status": "",
          Pending: "PENDING",
          Refunded: "REFUNDED",
          Rejected: "REJECTED",
        },
      },
    },
    {
      key: "actions",
      header: "Actions",
      enableColumnFilter: false,
      render: ({ row, callback }) =>
        generateActionButtonsJsx(row, callback, "refunds", false),
    },
  ],
  transactions: [
    {
      key: "id",
      header: "ID",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "type",
      header: "Type",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm text-left font-medium tracking-wide text-slate-900 dark:text-slate-100 font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "description",
      header: "Description",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm text-left font-medium tracking-wide text-slate-900 dark:text-slate-100 font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "createdAt",
      header: "Time",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {formatEpochToDateTime(info.getValue())}
        </h1>
      ),
    },
    {
      key: "isCredit",
      header: "isCredited",
      enableColumnFilter: true,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue() ? "Yes" : "No"}
        </h1>
      ),
      meta: {
        defaultValue: "All Status",
        filterVariant: "select",
        filterOptions: {
          "All Transactions": "",
          Credited: true,
          Debited: false,
        },
      },
    },
    {
      key: "now",
      header: "Before",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "points",
      header: "Points",
      enableColumnFilter: false,
      render: (info) => {
        const row = info.row.original;
        // console.log("Full row data:", row); // 👈 This logs all the column values for the row
        const isCredit = row.isCredit;
        const amount = row.points;

        return (
          <h1
            className={`inline-block px-3 py-0.5 text-sm font-semibold tracking-wide text-center font-mono rounded-full shadow-sm 
    ${
      isCredit
        ? "bg-green-50 text-green-700 border border-green-200"
        : "bg-red-50 text-red-700 border border-red-200"
    }`}
          >
            {isCredit ? `+${amount}` : `-${amount}`}
          </h1>
        );
      },
    },
    {
      key: "after",
      header: "After",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
  ],
  all_users: [
    {
      key: "id",
      header: "ID",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "username",
      header: "Username",
      enableColumnFilter: true,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
          {info.getValue()}
        </h1>
      ),
      meta: { placeholderName: "Find Username" },
    },
    {
      key: "name",
      header: "Name",
      enableColumnFilter: true,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
          {info.getValue()}
        </h1>
      ),
      meta: { placeholderName: "Find Name" },
    },
    // {
    //   key: "password",
    //   header: "Password",
    //   enableColumnFilter: false,
    //   render: (info) => (
    //     <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
    //       {info.getValue()}
    //     </h1>
    //   ),
    // },
    {
      key: "role",
      header: "Role",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "enabled",
      header: "Status",
      enableColumnFilter: true,
      render: (info) => {
        const isActive = info.getValue();
        return (
          <span
            className={`px-2 py-1 text-sm font-semibold rounded-full text-center ${
              isActive
                ? "bg-green-100 text-green-600"
                : "bg-red-100 text-red-600"
            }`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
      meta: {
        filterVariant: "select",
        filterOptions: {
          "All Status": "",
          Active: true,
          Inactive: false,
        },
      },
    },
    {
      key: "blocked",
      header: "Blocked",
      enableColumnFilter: false,
      render: (info) => {
        const isBlocked = info.getValue();
        return (
          <span
            className={`px-2 py-1 text-sm font-semibold rounded-full text-center ${
              !isBlocked
                ? "bg-green-100 text-green-600"
                : "bg-red-100 text-red-600"
            }`}
          >
            {!isBlocked ? "No" : "Yes"}
          </span>
        );
      },
    },
    {
      key: "parentType",
      header: "Parent Type",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "parent",
      header: "Parent Username",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      enableColumnFilter: false,
      render: ({ row, callback }) =>
        generateActionButtonsJsx(row, callback, "all_users", false),
    },
  ],
  telecom_subscribers: [
    {
      key: "groups",
      header: "Rooms",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
  ],
  telecom_superMasters: [
    {
      key: "points",
      header: "Points",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "usedPoints",
      header: "Used Points",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "mastersCount",
      header: "Masters",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "subscriberCount",
      header: "Subscribers",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      id: "lastRecharge",
      key: "lastRecharge",
      header: "Last Recharge",
      enableColumnFilter: false,
      render: (info) => {
        const value = info.getValue();
        if (!value) return null;

        return (
          <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
            {formatDateNew(value)}
          </h1>
        );
      },
    },
    {
      id: "lastSeen",
      key: "lastSeen",
      header: "Last Seen",
      enableColumnFilter: false,
      render: (info) => {
        const value = info.getValue();
        if (value === 0) return null; // or <></> if you prefer
        return (
          <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
            {formatEpochToDateTime(value)}
          </h1>
        );
      },
    },
    {
      key: "actions",
      header: "Actions",
      enableColumnFilter: false,
      render: ({ row, callback }) =>
        generateActionButtonsJsx(row, callback, "superMasters", true),
    },
  ],
  telecom_masters: [
    {
      key: "points",
      header: "Points",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "usedPoints",
      header: "Used Points",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "subscribersCount",
      header: "Subscribers",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
          {info.getValue()}
        </h1>
      ),
    },
    {
      id: "lastRecharge",
      key: "lastRecharge",
      header: "Last Recharge",
      enableColumnFilter: false,
      render: (info) => {
        const value = info.getValue();
        if (!value) return null;

        return (
          <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
            {formatDateNew(value)}
          </h1>
        );
      },
    },
    {
      id: "lastSeen",
      key: "lastSeen",
      header: "Last Seen",
      enableColumnFilter: false,
      render: (info) => {
        const value = info.getValue();
        if (value === 0) return null; // or <></> if you prefer
        return (
          <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
            {formatEpochToDateTime(value)}
          </h1>
        );
      },
    },
    {
      key: "actions",
      header: "Actions",
      enableColumnFilter: false,
      render: ({ row, callback }) =>
        generateActionButtonsJsx(row, callback, "telecom_masters", true),
    },
  ],
  zoom_accounts: [
    {
      key: "id",
      header: "ID",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    // {
    //   key: "email",
    //   header: "Email",
    //   enableColumnFilter: false,
    //   render: (info) => (
    //     <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
    //       {info.getValue()}
    //     </h1>
    //   ),
    // },
    // {
    //   key: "password",
    //   header: "Password",
    //   enableColumnFilter: false,
    //   render: (info) => (
    //     <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
    //       {info.getValue()}
    //     </h1>
    //   ),
    // },
    {
      key: "sdkKey",
      header: "SDK Key",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "sdkSecret",
      header: "SDK Secret",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "meetingSize",
      header: "Meetings",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "apiKey",
      header: "API Key",
      audioOnlyField: true,
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "apiSecret",
      header: "API Secret",
      audioOnlyField: true,
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "accountId",
      header: "Account ID",
      audioOnlyField: true,
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "createdAt",
      header: "Created At",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {formatDateNew(info.getValue())}
        </h1>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      enableColumnFilter: false,
      render: ({ row, callback }) =>
        generateActionButtonsJsx(row, callback, "zoom_accounts", false),
    },
  ],
  telecom_meetings: [
    {
      key: "id",
      header: "ID",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "name",
      header: "Name",
      enableColumnFilter: true,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
      meta: { placeholderName: "Find Name" },
    },
    {
      key: "email",
      header: "Email",
      audioOnlyField: true,
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    // {
    //   key: "password",
    //   header: "Password",
    //   enableColumnFilter: false,
    //   render: (info) => (
    //     <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
    //       {info.getValue()}
    //     </h1>
    //   ),
    // },
    {
      key: "meetingNumber",
      header: "Meeting Number",
      enableColumnFilter: true,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
      meta: { placeholderName: "Find Meeting Number" },
    },
    {
      key: "meetingPassword",
      header: "Meeting Password",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "zoomAccountId",
      header: "Account Id",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "hostUsername",
      header: "Host",
      audioOnlyField: true,
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "createdAt",
      header: "Created At",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {formatDateNew(info.getValue())}
        </h1>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      enableColumnFilter: false,
      render: ({ row, callback }) =>
        generateActionButtonsJsx(row, callback, "telecom_meetings", false),
    },
  ],
  hosts: [
    {
      id: "meeting_number",
      key: "meetingNumber",
      header: "Meeting Number",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      enableColumnFilter: false,
      render: ({
        row,
        callback,
        role,
        isVideoTenant,
        deviceLockRequired,
        themeData,
      }) =>
        generateActionButtonsJsx(
          row,
          callback,
          "hosts",
          true,
          role,
          isVideoTenant,
          deviceLockRequired,
          themeData,
        ),
    },
  ],
  telecom_hosts: [
    {
      id: "plan",
      key: "plan",
      header: "Plan",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    // {
    //   id: "uuid",
    //   key: "uuid",
    //   header: "UUID",
    //   audioField: false,
    //   deviceLockField: true,
    //   enableColumnFilter: false,
    //   render: (info) => (
    //     <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
    //       {info.getValue()}
    //     </h1>
    //   ),
    // },
    {
      id: "deviceInfo",
      key: "deviceInfo",
      header: "Device Info",
      audioField: false,
      deviceLockField: true,
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {info.getValue()}
        </h1>
      ),
    },
    {
      id: "startAt",
      key: "startAt",
      header: "Start At",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {formatDateNew(info.getValue())}
        </h1>
      ),
    },
    {
      id: "endAt",
      key: "endAt",
      header: "End At",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {formatDateNew(info.getValue())}
        </h1>
      ),
    },
    {
      id: "lastRecharge",
      key: "lastRecharge",
      header: "Last Recharge",
      enableColumnFilter: false,
      render: (info) => (
        <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono ">
          {formatDateNew(info.getValue())}
        </h1>
      ),
    },
    {
      id: "lastSeen",
      key: "lastSeen",
      header: "Last Seen",
      enableColumnFilter: false,
      render: (info) => {
        const value = info.getValue();
        if (value === 0) return null; // or <></> if you prefer
        return (
          <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
            {formatEpochToDateTime(value)}
          </h1>
        );
      },
    },
    {
      id: "lastPlatform",
      key: "lastPlatform",
      header: "Last Platform",
      enableColumnFilter: false,
      render: (info) => {
        const value = info.getValue();
        if (!value) return null; // handle null/undefined gracefully

        // Capitalize first letter
        const formattedValue =
          typeof value === "string"
            ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
            : value;

        return (
          <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
            {formattedValue}
          </h1>
        );
      },
    },
    {
      id: "endAtTimeLeft",
      key: "endAt",
      header: "Days Left",
      enableSorting: false,
      enableColumnFilter: false,
      render: (info) => {
        const row = info.row.original;
        const startRaw = row.startAt;
        const endRaw = row.endAt;

        if (!startRaw || !endRaw) return "";

        const parseDate = (raw) => {
          const str = String(raw);
          if (str.length !== 8) return null;

          const year = parseInt(str.substring(0, 4), 10);
          const month = parseInt(str.substring(4, 6), 10) - 1;
          const day = parseInt(str.substring(6, 8), 10);

          return new Date(Date.UTC(year, month, day)); // Use UTC for consistency
        };

        const startDate = parseDate(startRaw);
        const endDate = parseDate(endRaw);
        if (!startDate || !endDate) return "";

        const today = new Date();
        const todayUTC = new Date(
          Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()),
        );

        // Logic based on future start
        const baseDate = startDate > todayUTC ? startDate : todayUTC;
        const diffTime = endDate.getTime() - baseDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        let displayString = `${Math.abs(diffDays)} ${
          Math.abs(diffDays) === 1 ? "Day" : "Days"
        }`;
        if (diffDays < 0) displayString = `- ${displayString}`;

        const textColor =
          diffDays <= 0
            ? "bg-red-100 text-red-600"
            : diffDays <= 3
              ? "bg-blue-100 text-blue-600"
              : "bg-green-100 text-green-600";
        return (
          <h1
            className={`px-2 py-1 text-sm font-semibold rounded-full text-center ${textColor}`}
          >
            {displayString}
          </h1>
        );
      },
    },
    {
      key: "actions",
      header: "Actions",
      enableColumnFilter: false,
      render: ({
        row,
        callback,
        role,
        isVideoTenant,
        deviceLockRequired,
        themeData,
      }) =>
        generateActionButtonsJsx(
          row,
          callback,
          "telecom_hosts",
          true,
          role,
          isVideoTenant,
          deviceLockRequired,
          themeData,
        ),
    },
  ],
  telecom_managers: [
    {
      id: "lastSeen",
      key: "lastSeen",
      header: "Last Seen",
      enableColumnFilter: false,
      render: (info) => {
        const value = info.getValue();
        if (value === 0) return null; // or <></> if you prefer
        return (
          <h1 className="text-sm font-medium tracking-wide text-slate-900 dark:text-slate-100 text-center font-mono">
            {formatEpochToDateTime(value)}
          </h1>
        );
      },
    },
    {
      key: "actions",
      header: "Actions",
      enableColumnFilter: false,
      render: ({ row, callback }) =>
        generateActionButtonsJsx(row, callback, "telecom_managers", true),
    },
  ],
};

const reusableActions = [
  {
    label: "Edit",
    action: "edit",
    bgColor: "bg-sky-300",
    hoverColor: "bg-sky-400",
    jsx: (data, onClick) => (
      <li className="relative group order-1" key="edit">
        <button
          className="w-5 h-5 rounded-full bg-sky-300 hover:bg-sky-400 text-white flex items-center justify-center shadow-md p-0"
          onClick={() => onClick("edit", data)}
        >
          <PencilSquareIcon className="w-3.5 h-3.5 text-gray-800" />
        </button>
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
          <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
            Edit
          </div>
          <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
        </div>
      </li>
    ),
  },
  {
    label: "Deactivate",
    action: "deactivate",
    bgColor: "bg-red-400",
    hoverColor: "bg-red-500",
    jsx: (data, onClick) => (
      <li
        key="deactivate"
        onClick={() => onClick("deactivate", data)}
        className="inline-block cursor-pointer order-8"
      >
        <div className="flex items-center gap-2">
          {/* Button with tooltip */}
          <div className="relative group">
            <button
              className={`w-5 h-5 rounded-full ${
                data.enabled
                  ? "bg-red-300 hover:bg-red-400"
                  : "bg-green-400 hover:bg-green-500"
              } text-black flex items-center justify-center shadow-md p-0`}
            >
              {data.enabled ? (
                <LockClosedIcon className="w-3.5 h-3.5 text-gray-800" />
              ) : (
                <LockOpenIcon className="w-3.5 h-3.5 text-gray-800" />
              )}
            </button>

            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
              <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
                {data.enabled ? "Deactivate" : "Activate"}
              </div>
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
            </div>
          </div>

          {/* Divider */}
          <h1 className="text-sm text-gray-400">|</h1>
        </div>
      </li>
    ),
  },
  {
    label: "Share-WhatsApp",
    action: "share-whatsApp",
    bgColor: "bg-red-400",
    hoverColor: "bg-red-500",
    jsx: (
      data,
      onClick,
      accessorRole,
      isVideoTenant,
      isDeviceLockRequired,
      themeData,
    ) => (
      // console.log("ThemeData: ", themeData);
      <WhatsAppShareButton
        key={`whatsapp-${data.username}`}
        {...data}
        {...themeData?.downloadLinks}
      />
    ),
  },
  {
    label: "Universal Share",
    action: "share-universal",
    bgColor: "bg-indigo-500",
    hoverColor: "bg-indigo-600",
    jsx: (
      data,
      onClick,
      accessorRole,
      isVideoTenant,
      isDeviceLockRequired,
      themeData,
    ) => (
      <UniversalShareButton
        key={`universal-share-${data.username}`}
        {...data}
        {...themeData?.downloadLinks}
      />
    ),
  },
];

const tableActions = {
  owners: [
    {
      label: "Recharge",
      action: "owner_recharge",
      jsx: (data, onClick) => {
        return (
          <li className="relative group  order-2" key="owner_recharge">
            <button
              className="w-5 h-5 rounded-full bg-green-300 hover:bg-green-400 text-white flex items-center justify-center shadow-md p-0"
              onClick={() => onClick("owner_recharge", data)}
            >
              <BoltIcon className="w-3.5 h-3.5 text-gray-800" />
            </button>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
              <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
                Recharge
              </div>
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
            </div>
          </li>
        );
      },
    },
  ],
  all_users: [
    {
      action: "owner_sub_recharge",
      jsx: (data, onClick) => (
        <ActionButton
          onClick={() => onClick("owner_sub_recharge", data)}
          icon={BoltIcon}
          bg="bg-green-300"
          hoverBg="hover:bg-green-400"
          tooltip="Recharge"
          condition={data.role === "SUBSCRIBER" && !data.blocked}
        />
      ),
    },
    {
      action: "user_blocked",
      jsx: (data, onClick) => (
        <ActionButton
          onClick={() => onClick("user_blocked", data)}
          icon={!data.blocked ? NoSymbolIcon : LockOpenIcon}
          bg={!data.blocked ? "bg-red-300" : "bg-green-400"}
          hoverBg={!data.blocked ? "hover:bg-red-400" : "hover:bg-green-500"}
          tooltip={!data.blocked ? "Block" : "Unblock"}
        />
      ),
    },
  ],
  hosts: [
    {
      label: "Delete Host",
      action: "delete_hosts",
      bgColor: "bg-sky-300",
      hoverColor: "bg-sky-400",
      jsx: (data, onClick) => (
        <li className="relative group order-7" key="delete_hosts">
          <button
            className="w-5 h-5 rounded-full bg-red-300 hover:bg-red-400 text-white flex items-center justify-center shadow-md p-0"
            onClick={() => onClick("delete_hosts", data)}
          >
            <TrashIcon className="w-3.5 h-3.5 text-gray-800" />
          </button>
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
            <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
              Delete Host
            </div>
            <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
          </div>
        </li>
      ),
    },
    {
      label: "Unlink Meeting",
      action: "unlink_meeting",
      bgColor: "bg-sky-300",
      hoverColor: "bg-sky-400",
      jsx: (data, onClick) => {
        if (data.meetingNumber) {
          return (
            <li className="relative group order-7" key="unlink_meeting">
              <button
                className="w-5 h-5 rounded-full bg-amber-300 hover:bg-amber-400 text-white flex items-center justify-center shadow-md p-0"
                onClick={() => onClick("unlink_meeting", data)}
              >
                <LinkSlashIcon className="w-3.5 h-3.5 text-gray-800" />
              </button>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
                <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
                  Unlink Meeting
                </div>
                <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
              </div>
            </li>
          );
        }
        return null;
      },
    },
    {
      label: "Link Meeting",
      action: "link_meeting",
      bgColor: "bg-sky-300",
      hoverColor: "bg-sky-400",
      jsx: (data, onClick) => {
        if (!data.meetingNumber) {
          return (
            <li className="relative group order-7" key="link_meeting">
              <button
                className="w-5 h-5 rounded-full bg-green-300 hover:bg-green-400 text-white flex items-center justify-center shadow-md p-0"
                onClick={() => onClick("link_meeting", data)}
              >
                <LinkIcon className="w-3.5 h-3.5 text-gray-800" />
              </button>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
                <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
                  Link Meeting
                </div>
                <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
              </div>
            </li>
          );
        }
        return null;
      },
    },
  ],
  telecom_hosts: [
    {
      label: "Recharge",
      action: "recharge",
      jsx: (data, onClick) => {
        // Hide Recharge button for Multi plans
        if (
          data?.plan?.toLowerCase().includes("multi") ||
          data?.plan?.toLowerCase().startsWith("m-")
        ) {
          return null;
        }

        return (
          <li className="relative group order-2" key="recharge">
            <button
              className="w-5 h-5 rounded-full bg-green-300 hover:bg-green-300 text-white flex items-center justify-center shadow-md p-0"
              onClick={() => onClick("recharge", data)}
            >
              <BoltIcon className="w-3.5 h-3.5 text-gray-800" />
            </button>

            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
              <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
                Recharge
              </div>
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
            </div>
          </li>
        );
      },
    },
    {
      label: "Adjust",
      action: "adjust",
      jsx: (data, onClick, role) => {
        if (role === "OWNER") {
          return (
            <li className="relative group order-6" key="adjust">
              <button
                className="w-5 h-5 rounded-full bg-purple-300 hover:bg-purple-400 text-white flex items-center justify-center shadow-md p-0"
                onClick={() => onClick("adjust", data)}
              >
                <AdjustmentsHorizontalIcon className="w-3.5 h-3.5 text-gray-800" />
              </button>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
                <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
                  Adjust
                </div>
                <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
              </div>
            </li>
          );
        }
        return null;
      },
    },
    {
      label: "Device Change",
      action: "device_change",
      jsx: (data, onClick, role, isVideoTenant, deviceLockRequired) => {
        if (
          role === "OWNER" &&
          !isVideoTenant &&
          deviceLockRequired &&
          data.deviceInfo
        ) {
          return (
            <li className="relative group order-6" key="device_change">
              <button
                className="w-5 h-5 rounded-full bg-teal-300 hover:bg-teal-400 text-white flex items-center justify-center shadow-md p-0"
                onClick={() => onClick("device_change", data)}
              >
                <DevicePhoneMobileIcon className="w-3.5 h-3.5 text-gray-800" />
              </button>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
                <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
                  Device Change
                </div>
                <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
              </div>
            </li>
          );
        }
        return null;
      },
    },
    {
      label: "Refund",
      action: "subscriberRefund",
      jsx: (data, onClick) => {
        // Parse YYYYMMDD long to Date
        const lastRechargeStr = data.lastRecharge.toString();
        const year = parseInt(lastRechargeStr.substring(0, 4), 10);
        const month = parseInt(lastRechargeStr.substring(4, 6), 10) - 1; // JS months are 0-based
        const day = parseInt(lastRechargeStr.substring(6, 8), 10);
        const lastRechargeDate = new Date(year, month, day);

        // Calculate difference in days
        const today = new Date();
        const diffInTime = today.getTime() - lastRechargeDate.getTime();
        const diffInDays = diffInTime / (1000 * 3600 * 24);

        if (diffInDays > 7 || !data.canRefund || data.refundableMonths < 1) {
          return null;
        }

        return (
          <li className="relative group order-5" key="subscriberRefund">
            <button
              className="w-5 h-5 rounded-full bg-red-400 hover:bg-red-500 text-white flex items-center justify-center shadow-md p-0"
              onClick={() => onClick("subscriberRefund", data)}
            >
              <ArrowUturnLeftIcon className="w-3.5 h-3.5 text-gray-800" />
            </button>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
              <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
                Refund
              </div>
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
            </div>
          </li>
        );
      },
    },
    {
      label: "Delete Subscriber",
      action: "delete_host",
      bgColor: "bg-sky-300",
      hoverColor: "bg-sky-400",
      jsx: (data, onClick) => {
        const parseEndAtToDate = (endAt) => {
          const str = String(endAt);
          if (str.length !== 8) return null;
          const year = Number(str.slice(0, 4));
          const month = Number(str.slice(4, 6)) - 1; // JS months are 0-indexed
          const day = Number(str.slice(6, 8));
          return new Date(year, month, day);
        };

        const endAtDate = parseEndAtToDate(data.endAt);
        if (!endAtDate) return null;

        const now = new Date();
        const sixtyDaysAgo = new Date(now);
        sixtyDaysAgo.setDate(now.getDate() - 7);

        if (endAtDate > sixtyDaysAgo) {
          return null;
        }

        return (
          <li className="relative group order-7" key="delete_host">
            <button
              className="w-5 h-5 rounded-full bg-red-300 hover:bg-red-400 text-white flex items-center justify-center shadow-md p-0"
              onClick={() => onClick("delete_host", data)}
            >
              <TrashIcon className="w-3.5 h-3.5 text-gray-800" />
            </button>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
              <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
                Delete Subscriber
              </div>
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
            </div>
          </li>
        );
      },
    },
    {
      label: "One Month Recharge",
      action: "one_month_recharge",
      bgColor: "bg-sky-300",
      hoverColor: "bg-sky-400",
      jsx: (data, onClick) => {
        if (
          data?.plan?.toLowerCase().includes("multi") ||
          data?.plan?.toLowerCase().startsWith("m-")
        ) {
          return null;
        }

        return (
          <li
            className="relative group order-3"
            key={`one_month_recharge-${data.username}`}
          >
            <button
              className="w-5 h-5 rounded-full bg-green-300 hover:bg-green-400 text-white flex items-center justify-center shadow-md p-0"
              onClick={() => onClick("one_month_recharge", data)}
            >
              <h1 className="text-black text-xs font-semibold">1</h1>
            </button>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
              <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
                1 Month Recharge
              </div>
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
            </div>
          </li>
        );
      },
    },
    {
      label: "Three Months Recharge",
      action: "three_months_recharge",
      bgColor: "bg-sky-300",
      hoverColor: "bg-sky-400",
      jsx: (data, onClick) => {
        if (
          data?.plan?.toLowerCase().includes("multi") ||
          data?.plan?.toLowerCase().startsWith("m-")
        ) {
          return null;
        }

        return (
          <li
            className="relative group order-4"
            key={`three_months_recharge-${data.username}`}
          >
            <div className="flex items-center gap-2">
              <button
                className="w-5 h-5 rounded-full bg-green-300 hover:bg-green-400 text-white flex items-center justify-center shadow-md p-0"
                onClick={() => onClick("three_months_recharge", data)}
              >
                <h1 className="text-black text-xs font-semibold">3</h1>
              </button>

              <h1 className="text-sm text-gray-400">|</h1>
            </div>

            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
              <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
                3 Months Recharge
              </div>
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
            </div>
          </li>
        );
      },
    },
  ],
  telecom_managers: [
    {
      label: "Delete Manager",
      action: "delete_manager",
      bgColor: "bg-sky-300",
      hoverColor: "bg-sky-400",
      jsx: (data, onClick) => (
        <li className="relative group order-7" key="delete_manager">
          <button
            className="w-5 h-5 rounded-full bg-red-300 hover:bg-red-400 text-white flex items-center justify-center shadow-md p-0"
            onClick={() => onClick("delete_manager", data)}
          >
            <TrashIcon className="w-3.5 h-3.5 text-gray-800" />
          </button>
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
            <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
              Delete Manager
            </div>
            <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
          </div>
        </li>
      ),
    },
  ],
  telecom_masters: [
    {
      label: "Points",
      action: "rechargePoints",
      jsx: (data, onClick) => (
        <li className="relative group  order-2" key="rechargePoints">
          <button
            className="w-5 h-5 rounded-full bg-cyan-500 hover:bg-cyan-600 text-white flex items-center justify-center shadow-md p-0"
            onClick={() => onClick("rechargePoints", data)}
          >
            <BoltIcon className="w-3.5 h-3.5 text-gray-800" />
          </button>
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
            <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
              Points
            </div>
            <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
          </div>
        </li>
      ),
    },
    {
      label: "Settle",
      action: "settle_used_points",
      jsx: (data, onClick) => (
        <li className="relative group  order-2" key="settle_used_points">
          <button
            className="w-5 h-5 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center shadow-md p-0"
            onClick={() => onClick("settle_used_points", data)}
          >
            <PlusIcon className="w-3.5 h-3.5 text-gray-800" />
          </button>
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
            <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
              Settle
            </div>
            <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
          </div>
        </li>
      ),
    },
    {
      label: "Reverse Points",
      action: "reverse_points",
      jsx: (data, onClick) => {
        if (data.points === 0) {
          return null; // or return some fallback like <li>-</li>
        }
        return (
          <li className="relative group order-3" key="reverse_points">
            <button
              className="w-5 h-5 rounded-full bg-teal-500 hover:bg-teal-600 text-white flex items-center justify-center shadow-md p-0 transform transition-transform duration-200 hover:scale-110"
              onClick={() => onClick("reverse_points", data)}
            >
              <ArrowUturnLeftIcon className="w-3.5 h-3.5 text-gray-800" />
            </button>

            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
              <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
                Reverse Points
              </div>
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
            </div>
          </li>
        );
      },
    },
    // {
    //   label: "Delete Master",
    //   action: "delete_master",
    //   bgColor: "bg-sky-300",
    //   hoverColor: "bg-sky-400",
    //   jsx: (data, onClick) => (
    //     <li className="relative group" key="Delete Host">
    //       <button
    //         className="w-5 h-5 rounded-full bg-red-300 hover:bg-red-400 text-white flex items-center justify-center shadow-md p-0"
    //         onClick={() => onClick("delete_master", data)}
    //       >
    //         <span className="text-xs text-gray-800 font-semibold leading-none">
    //           D
    //         </span>
    //       </button>
    //       <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
    //         <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
    //           Delete Master
    //         </div>
    //         <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
    //       </div>
    //     </li>
    //   ),
    // },
  ],
  superMasters: [
    {
      label: "Points",
      action: "rechargeSuperMasterPoints",
      jsx: (data, onClick) => (
        <li className="relative group  order-2" key="rechargeSuperMasterPoints">
          <button
            className="w-5 h-5 rounded-full bg-cyan-500 hover:bg-cyan-600 text-white flex items-center justify-center shadow-md p-0"
            onClick={() => onClick("rechargeSuperMasterPoints", data)}
          >
            <BoltIcon className="w-3.5 h-3.5 text-gray-800" />
          </button>
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
            <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
              Recharge
            </div>
            <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
          </div>
        </li>
      ),
    },
    {
      label: "Settle",
      action: "settle_sm_used_points",
      jsx: (data, onClick) => (
        <li className="relative group  order-2" key="settle_sm_used_points">
          <button
            className="w-5 h-5 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center shadow-md p-0"
            onClick={() => onClick("settle_sm_used_points", data)}
          >
            <PlusIcon className="w-3.5 h-3.5 text-gray-800" />
          </button>
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
            <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
              Settle
            </div>
            <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
          </div>
        </li>
      ),
    },
    {
      label: "Reverse Points",
      action: "reverse_super_master_points",
      jsx: (data, onClick) => {
        if (data.points === 0) {
          return null; // or return some fallback like <li>-</li>
        }
        return (
          <li
            className="relative group  order-3"
            key="reverse_super_master_points"
          >
            <button
              className="w-5 h-5 rounded-full bg-teal-500 hover:bg-teal-600 text-white flex items-center justify-center shadow-md p-0 transform transition-transform duration-200 hover:scale-110"
              onClick={() => onClick("reverse_super_master_points", data)}
            >
              <ArrowUturnLeftIcon className="w-3.5 h-3.5 text-gray-800" />
            </button>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
              <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
                Reverse Points
              </div>
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
            </div>
          </li>
        );
      },
    },
    // {
    //   label: "Delete Super Master",
    //   action: "delete_super_master",
    //   bgColor: "bg-sky-300",
    //   hoverColor: "bg-sky-400",
    //   jsx: (data, onClick) => (
    //     <li className="relative group" key="delete_super_master">
    //       <button
    //         className="w-5 h-5 rounded-full bg-red-300 hover:bg-red-400 text-white flex items-center justify-center shadow-md p-0"
    //         onClick={() => onClick("delete_super_master", data)}
    //       >
    //         <span className="text-xs text-gray-800 font-semibold leading-none">
    //           D
    //         </span>
    //       </button>
    //       <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
    //         <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
    //           Delete Super Master
    //         </div>
    //         <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
    //       </div>
    //     </li>
    //   ),
    // },
  ],
  refunds: [
    {
      label: "Accept",
      action: "acceptRefund",
      jsx: (data, onClick) => {
        if (data.status !== "PENDING") {
          return null;
        }
        return (
          <li className="relative group" key="acceptRefund">
            <button
              className="w-5 h-5 rounded-full bg-orange-300 hover:bg-orange-400  text-white flex items-center justify-center shadow-md p-0"
              onClick={() => onClick("acceptRefund", data)}
            >
              <ArrowsRightLeftIcon className="w-3.5 h-3.5 text-gray-800" />
            </button>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
              <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
                Action
              </div>
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
            </div>
          </li>
        );
      },
    },
  ],
  zoom_accounts: [
    {
      label: "Edit",
      action: "edit",
      bgColor: "bg-sky-300",
      hoverColor: "bg-sky-400",
      jsx: (data, onClick) => (
        <li className="relative group" key="zoom-edit">
          <button
            className="w-5 h-5 rounded-full bg-sky-300 hover:bg-sky-400 text-white flex items-center justify-center shadow-md p-0"
            onClick={() => onClick("zoom_account_edit", data)}
          >
            <PencilSquareIcon className="w-3.5 h-3.5 text-gray-800" />
          </button>
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
            <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
              Edit
            </div>
            <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
          </div>
        </li>
      ),
    },
    {
      label: "Delete Zoom",
      action: "delete_zoom_account",
      bgColor: "bg-sky-300",
      hoverColor: "bg-sky-400",
      jsx: (data, onClick) => {
        // Only show if there's a meeting assigned
        return (
          <li className="relative group" key="delete_zoom_account">
            <button
              className="w-5 h-5 rounded-full bg-red-300 hover:bg-red-400 text-white flex items-center justify-center shadow-md p-0"
              onClick={() => onClick("delete_zoom_account", data)}
            >
              <TrashIcon className="w-3.5 h-3.5 text-gray-800" />
            </button>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
              <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
                Delete Zoom
              </div>
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
            </div>
          </li>
        );
      },
    },
  ],
  telecom_meetings: [
    {
      label: "Edit",
      action: "edit_meeting",
      bgColor: "bg-sky-300",
      hoverColor: "bg-sky-400",
      jsx: (data, onClick) => (
        <li className="relative group" key="edit_meeting">
          <button
            className="w-5 h-5 rounded-full bg-sky-300 hover:bg-sky-400 text-white flex items-center justify-center shadow-md p-0"
            onClick={() => onClick("edit_meeting", data)}
          >
            <PencilSquareIcon className="w-3.5 h-3.5 text-gray-800" />
          </button>
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
            <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
              Edit
            </div>
            <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
          </div>
        </li>
      ),
    },
    {
      label: "Delete Meeting",
      action: "delete_meeting",
      bgColor: "bg-sky-300",
      hoverColor: "bg-sky-400",
      jsx: (data, onClick) => (
        <li className="relative group" key="delete_meeting">
          <button
            className="w-5 h-5 rounded-full bg-red-300 hover:bg-red-400 text-white flex items-center justify-center shadow-md p-0"
            onClick={() => onClick("delete_meeting", data)}
          >
            <TrashIcon className="w-3.5 h-3.5 text-gray-800" />
          </button>
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
            <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg w-auto text-center">
              Delete Meeting
            </div>
            <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
          </div>
        </li>
      ),
    },
  ],
};

// export const generateTableColumns = (
//   tableName,
//   isExtendingReusabeColumns,
//   jwt
// ) => {
//   const isDeviceLockRequired = jwt.dl;
//   const isAudioFieldsRequired = !jwt.iv;
//   const specificColumns = tableColumns[tableName] || [];

//   // Decide which columns to use
//   const baseColumns = isExtendingReusabeColumns
//     ? [...reusableColumns, ...specificColumns]
//     : specificColumns;

//   return baseColumns
//     .filter(({ deviceLockField, audioOnlyField }) => {
//       if (deviceLockField === undefined && audioOnlyField === undefined) {
//         return true; // always show columns without deviceLockField
//       }
//       return isDeviceLockRequired; // show deviceLockField only if required
//     })
//     .map(
//       (
//         { key, header, enableColumnFilter, enableSorting, render, meta },
//         index
//       ) =>
//         columnHelper.accessor(key, {
//           header,
//           id: header === "Time Left" ? key + "_" + index : key,
//           cell: render,
//           enableColumnFilter,
//           enableSorting,
//           meta,
//         })
//     );
// };

export const generateTableColumns = (
  tableName,
  isExtendingReusabeColumns,
  jwt,
) => {
  const isDeviceLockRequired = jwt.dl;
  const isAudioFieldsRequired = !jwt.iv;
  const specificColumns = tableColumns[tableName] || [];

  // Combine reusable + specific columns if needed
  const baseColumns = isExtendingReusabeColumns
    ? [...reusableColumns, ...specificColumns]
    : specificColumns;

  return baseColumns
    .filter(({ deviceLockField, audioOnlyField }) => {
      // Always show columns with no special field flags
      if (deviceLockField === undefined && audioOnlyField === undefined) {
        return true;
      }

      // Show if deviceLockField condition is met
      if (deviceLockField) {
        return isDeviceLockRequired;
      }

      // Show if audioOnlyField condition is met
      if (audioOnlyField) {
        return isAudioFieldsRequired;
      }

      return false;
    })
    .map(
      (
        { key, header, enableColumnFilter, enableSorting, render, meta },
        index,
      ) =>
        columnHelper.accessor(key, {
          header,
          id: header === "Time Left" ? `${key}_${index}` : key,
          cell: render,
          enableColumnFilter,
          enableSorting,
          meta,
        }),
    );
};

const generateActionButtonsJsx = (
  data,
  callback,
  tableName,
  isExtendingReusabeActions,
  role,
  isVideoTenant,
  deviceLockRequired,
  themeData,
) => {
  // console.log("ThemeData: ", themeData);
  const actions = generateActionButtons(tableName, isExtendingReusabeActions); // Call the function correctly

  return (
    <ul
      key={tableName}
      className={`flex space-x-2 items-center ${
        tableName === "telecom_hosts" ? "justify-start" : "justify-center"
      }`}
    >
      {actions.map(
        (item) =>
          item.jsx &&
          item.jsx(
            data,
            callback,
            role,
            isVideoTenant,
            deviceLockRequired,
            themeData,
          ),
      )}
    </ul>
  );
};

export const generateActionButtons = (tableName, isExtendingReusabeActions) => {
  if (!isExtendingReusabeActions) return tableActions[tableName] || [];
  let actionsTableData = tableActions[tableName] || [];
  return [...reusableActions, ...actionsTableData];
};

function ActionButton({
  onClick,
  icon: Icon,
  bg,
  hoverBg,
  tooltip,
  condition = true,
}) {
  if (!condition) return null;

  return (
    <li className="relative group order-2">
      <button
        className={`w-5 h-5 rounded-full ${bg} ${hoverBg} text-white flex items-center justify-center shadow-md p-0`}
        onClick={onClick}
      >
        <Icon className="w-3.5 h-3.5 text-gray-800" />
      </button>

      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:flex flex-col items-center">
        <div className="bg-gray-800/75 text-white text-xs rounded px-2 py-1 shadow-lg text-center">
          {tooltip}
        </div>
        <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
      </div>
    </li>
  );
}
